import { ChangeEvent, Children, FC, MouseEvent, cloneElement, isValidElement } from 'react';

import { StyledTab, StyledTabPanel, TabsContainer, TabsWrapper, Underline } from './tabs.styled';
import { ITabListProps, ITabPanelProps, ITabProps } from './types';

interface ITabPropsAndOnClick extends ITabProps {
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const TabList: FC<ITabListProps> = ({ children, selectedIndex, onChange, className }) => {
  if (!children || Children.count(children) <= 0) {
    return null;
  }

  // calculating percentage of tab width
  const tabSlideWidth = 100 / children.length;

  const tabs = Children.map(children, (child, indx) => {
    if (!isValidElement(child)) {
      return child;
    }

    const onClick = (e: ChangeEvent<{}>) => {
      onChange(e, indx);
    };

    return cloneElement<ITabPropsAndOnClick>(child, { onClick });
  });

  return (
    <TabsContainer className={className}>
      <TabsWrapper role="tablist">{tabs}</TabsWrapper>
      <Underline tabSlideWidth={tabSlideWidth} idx={selectedIndex} />
    </TabsContainer>
  );
};

export const Tab: FC<ITabPropsAndOnClick> = ({
  id,
  label,
  disabled,
  onClick,
  selected,
  'aria-controls': ariaControls,
  'data-testid': dataTestId,
}) => {
  return (
    <StyledTab
      id={id}
      role="tab"
      $selected={selected}
      onClick={onClick}
      disabled={disabled}
      aria-selected={selected}
      aria-controls={ariaControls}
      data-testid={dataTestId}
    >
      {label}
    </StyledTab>
  );
};

export const TabPanel: FC<ITabPanelProps> = ({
  id,
  selected,
  children,
  className,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
}) => {
  return (
    <StyledTabPanel
      className={className}
      id={id}
      key={`tab-panel_${id}`}
      hidden={!selected}
      $selected={selected}
      role="tabpanel"
      tabIndex={0}
      aria-labelledby={ariaLabelledBy}
      data-testid={dataTestId}
    >
      {children}
    </StyledTabPanel>
  );
};
